// STUDIO-BR-TX3-SHUTTER-CAPTURE-1 · 모바일 Studio 캡처 버튼 (BR-TX3 하단 셔터의 실제 가상 터치 위치에 배치).
// 셔터 입력 엔진을 새로 만들지 않고, 셔터가 누르는 좌표에 버튼을 두어 기존 3·2·1 빠른등록 캡처를 실행한다.
import React, { useEffect, useState } from 'react';
import { Camera, X } from 'lucide-react';

const POS_KEY = 'livemoa.shutterBtnPos.v1';
const SIZE = 44;

const loadPos = () => { try { return JSON.parse(localStorage.getItem(POS_KEY)) || null; } catch { return null; } };
const clamp = (v, lo, hi) => Math.min(Math.max(v, lo), hi);

// 저장 비율(화면 기준) → 현재 화면 좌표. 저장값 없으면 우측 중간(기존 우측 버튼줄 y<47% · 하단 버튼 회피).
export const shutterButtonRect = (saved, vw, vh) => {
  const cx = saved ? saved.x * vw : vw - 12 - SIZE / 2;
  const cy = saved ? saved.y * vh : vh * 0.58;
  const left = clamp(cx - SIZE / 2, 8, vw - SIZE - 8);
  const top = clamp(cy - SIZE / 2, 8, vh - SIZE - 8);
  return { left, top, right: left + SIZE, bottom: top + SIZE, size: SIZE };
};

export const useShutterButtonRect = () => {
  const [saved, setSaved] = useState(loadPos);
  const [vp, setVp] = useState({ w: window.innerWidth, h: window.innerHeight });
  useEffect(() => {
    const on = () => setVp({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener('resize', on);
    return () => window.removeEventListener('resize', on);
  }, []);
  const save = (p) => {
    setSaved(p);
    try { if (p) localStorage.setItem(POS_KEY, JSON.stringify(p)); else localStorage.removeItem(POS_KEY); } catch { /* 메모리만 */ }
  };
  return { rect: shutterButtonRect(saved, vp.w, vp.h), saved, save };
};

export const ShutterCaptureButton = ({ rect, onCapture, countdown }) => (
  <button type="button" data-shutter-capture="1" onClick={onCapture}
          className="lg:hidden fixed z-[45] rounded-full bg-white/90 text-black shadow-lg border-2 border-[#FF3B30] inline-flex flex-col items-center justify-center pointer-events-auto active:scale-95 transition-transform"
          style={{ left: rect.left, top: rect.top, width: rect.size, height: rect.size }}
          aria-label="상품 캡처" data-testid="live-console-shutter-capture-btn">
    {countdown != null && countdown > 0
      ? <span className="text-base font-black text-[#FF3B30]" data-testid="live-console-shutter-capture-count">{countdown}</span>
      : <><Camera size={16} /><span className="text-[8px] font-black leading-none mt-0.5">캡처</span></>}
  </button>
);

// 위치 맞추기: 다음 1회 입력(셔터 또는 손가락)의 실제 화면 좌표·입력 종류를 기록하고 그 위치로 버튼 이동.
export const ShutterCalibrator = ({ onDone, onClose }) => {
  const [diag, setDiag] = useState(null);
  const onDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (diag) return;
    const under = document.elementsFromPoint(e.clientX, e.clientY).find((el) => !e.currentTarget.contains(el));
    const d = {
      type: e.pointerType || 'unknown', x: Math.round(e.clientX), y: Math.round(e.clientY),
      vw: window.innerWidth, vh: window.innerHeight,
      standalone: window.matchMedia('(display-mode: standalone)').matches || window.matchMedia('(display-mode: fullscreen)').matches,
      under: under ? (under.dataset?.testid || under.tagName.toLowerCase()) : '-',
    };
    setDiag(d);
    onDone({ x: e.clientX / window.innerWidth, y: e.clientY / window.innerHeight, type: d.type });
  };
  return (
    <div className="lg:hidden fixed inset-0 z-[90] bg-black/60 text-white flex flex-col items-center justify-center gap-3 p-6 text-center"
         onPointerDown={onDown} data-testid="live-console-shutter-calibrator">
      <button type="button" onPointerDown={(e) => { e.stopPropagation(); onClose(); }}
              className="absolute top-4 right-4 p-2 rounded-full bg-white/20" aria-label="닫기" data-testid="live-console-shutter-calibrator-close"><X size={18} /></button>
      {!diag ? (
        <>
          <p className="text-lg font-black">리모컨 하단 셔터를 1번 누르세요</p>
          <p className="text-xs text-white/70">눌린 위치에 캡처 버튼을 둡니다 · 리모컨 조작은 잠시 꺼집니다</p>
        </>
      ) : (
        <div className="text-xs space-y-1" data-testid="live-console-shutter-calibrator-result">
          <p className="text-base font-black">위치 저장됨</p>
          <p>입력 종류 {diag.type} · 좌표 {diag.x}, {diag.y} · 화면 {diag.vw}×{diag.vh}</p>
          <p>전체화면 앱 {diag.standalone ? '예' : '아니오'} · 원래 대상 {diag.under}</p>
          <p className="text-white/60">화면이 반응하지 않으면 셔터 입력이 브라우저로 들어오지 않은 것입니다</p>
          <button type="button" onPointerDown={(e) => { e.stopPropagation(); onClose(); }}
                  className="mt-2 px-4 py-2 rounded-full bg-white text-black font-black" data-testid="live-console-shutter-calibrator-ok">확인</button>
        </div>
      )}
    </div>
  );
};
